import { type SiteLocale } from "../contexts/LocaleContext";

type HomeCard = {
  title: string;
  body: string;
};

type HomeMetric = {
  title: string;
  body: string;
};

type HomeProductHighlight = {
  label: string;
  title: string;
  body: string;
};

type HomeCopy = {
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string;
  heroBadge: string;
  heroTitle: string;
  heroTitleAccent: string;
  heroDescription: string;
  primaryCta: string;
  secondaryCta: string;
  heroMetrics: HomeMetric[];
  panelTag: string;
  panelTitle: string;
  panelBody: string;
  deliveryFlow: string[];
  missionKicker: string;
  missionTitle: string;
  missionBody: string;
  whatWeDo: HomeCard[];
  futureKicker: string;
  futureTitle: string;
  futureBody: string;
  servicesKicker: string;
  servicesTitle: string;
  servicesBody: string;
  services: HomeCard[];
  productsKicker: string;
  productKicker: string;
  productTitle: string;
  productBody: string;
  productHighlights: HomeProductHighlight[];
  productPills: string[];
  hireKicker: string;
  hireTitle: string;
  hireBody: string;
  hireHighlights: HomeProductHighlight[];
  hirePills: string[];
  aiKicker: string;
  aiTitle: string;
  aiBody: string;
  aiHighlights: HomeProductHighlight[];
  aiPills: string[];
  learnMore: string;
  contactsKicker: string;
  contactsTitle: string;
  contactsBody: string;
  formTitle: string;
};

type AboutHighlight = {
  value: string;
  label: string;
  body: string;
};

type StoryCard = {
  eyebrow: string;
  title: string;
  body: string;
};

type Principle = {
  title: string;
  body: string;
};

type AboutCopy = {
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string;
  badge: string;
  heroTitle: string;
  heroBody: string;
  highlights: AboutHighlight[];
  panelLabel: string;
  panelTitle: string;
  panelBody: string;
  focusPoints: string[];
  storyKicker: string;
  storyTitle: string;
  storyCards: StoryCard[];
  positionKicker: string;
  positionTitle: string;
  positionBody: string;
  principles: Principle[];
  nextKicker: string;
  ctaTitle: string;
  ctaBody: string;
  careersCta: string;
  contactCta: string;
};

type AiPainPoint = {
  title: string;
  body: string;
  response: string;
};

type AiProcessStep = {
  title: string;
  body: string;
};

type AiTier = {
  label: string;
  title: string;
  body: string;
  outcome: string;
};

type AiCopy = {
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string;
  badge: string;
  heroTitle: string;
  heroBody: string;
  primaryCta: string;
  secondaryCta: string;
  expertKicker: string;
  expertName: string;
  expertRole: string;
  expertBody: string;
  expertHighlights: string[];
  painKicker: string;
  painTitle: string;
  painPoints: AiPainPoint[];
  processKicker: string;
  processTitle: string;
  processBody: string;
  processSteps: AiProcessStep[];
  tiersKicker: string;
  tiersTitle: string;
  tiersBody: string;
  tiers: AiTier[];
  ctaKicker: string;
  ctaTitle: string;
  ctaBody: string;
  ctaPrimary: string;
  ctaSecondary: string;
};

type PolicySection = {
  id: string;
  label: string;
};

type PolicyCopy = {
  badge: string;
  title: string;
  pageTitle: string;
  description: string;
  summaryTitle: string;
  summaryBody: string;
  sections: PolicySection[];
  content: string;
};

export const HOME_COPY: Record<SiteLocale, HomeCopy> = {
  ru: {
    pageTitle: "CatLogic — цифровые продукты, HRMS Stafflow и AI-внедрение",
    metaDescription:
      "CatLogic — студия цифровых продуктов из Минска: разработка ПО и UX/UI под ключ, HRMS-платформа Stafflow и практическое AI-внедрение для бизнеса.",
    metaKeywords:
      "CatLogic, Stafflow, HRMS, разработка ПО, UX/UI дизайн, мобильные приложения, AI внедрение, автоматизация HR, продуктовая студия Минск",
    heroBadge: "CatLogic.tech • product engineering studio",
    heroTitle: "Цифровые продукты,",
    heroTitleAccent: "которые усиливают команды",
    heroDescription:
      "Проектируем интерфейсы, разрабатываем платформы и внедряем современные технологические подходы, чтобы бизнес быстрее переходил от идеи к работающему результату.",
    primaryCta: "Обсудить проект",
    secondaryCta: "Смотреть услуги",
    heroMetrics: [
      {
        title: "Full-cycle delivery",
        body: "От discovery и дизайна до запуска и развития продукта.",
      },
      {
        title: "Remote-first workflow",
        body: "Гибко работаем с командами из разных стран и часовых поясов.",
      },
      {
        title: "AI-minded execution",
        body: "Используем современные подходы, чтобы усиливать команды и процессы.",
      },
    ],
    panelTag: "Как мы работаем",
    panelTitle: "От идеи до запуска без лишней инерции",
    panelBody:
      "CatLogic соединяет аналитику, дизайн, разработку и внедрение в один рабочий цикл, чтобы решения быстрее доходили до реального эффекта.",
    deliveryFlow: [
      "Исследование, стратегия и архитектура",
      "UX/UI дизайн и проектирование интерфейсов",
      "Разработка, интеграции и облачная инфраструктура",
      "Запуск, обучение команды и сопровождение",
    ],
    missionKicker: "Миссия",
    missionTitle: "Исключительное усиление человеческих способностей через технологии",
    missionBody:
      "В CatLogic мы превращаем идеи в практичные ИТ-решения, которые помогают компаниям расти, работать быстрее и чувствовать уверенность в своих цифровых инструментах.",
    whatWeDo: [
      {
        title: "Инновации и качество",
        body: "Мы исследуем новые технологии и методологии, чтобы решения CatLogic соответствовали современным требованиям и были готовы к будущим вызовам.",
      },
      {
        title: "Скорость и гибкость",
        body: "Запускаем проекты быстро и без потери качества, оставаясь адаптивными к изменениям продукта, бизнеса и рынка.",
      },
      {
        title: "Партнерство и доверие",
        body: "Смотрим на каждую задачу как на долгосрочное сотрудничество и строим процессы вокруг прозрачности, уважения и результата.",
      },
      {
        title: "Профессионализм и мастерство",
        body: "Сильная инженерная и продуктовая экспертиза позволяет нам принимать зрелые технические решения и доводить их до запуска.",
      },
    ],
    futureKicker: "Вектор",
    futureTitle: "На пути к будущему",
    futureBody:
      "Мы верим в потенциал технологий менять рынок к лучшему. Поэтому CatLogic строит решения, которые отвечают текущим задачам бизнеса и при этом закладывают запас для следующего этапа роста.",
    servicesKicker: "Услуги",
    servicesTitle: "От исследования до поддержки продукта",
    servicesBody:
      "Помогаем запускать и развивать цифровые сервисы, комбинируя продуктовую логику, дизайн, инженерную дисциплину и инфраструктурную надежность.",
    services: [
      {
        title: "Проектирование и анализ",
        body: "Перед разработкой мы формируем понятную картину продукта: уточняем требования, пользовательские сценарии, ограничения и архитектурные решения.",
      },
      {
        title: "Исследования рынка",
        body: "Анализируем аудиторию, конкурентную среду и рыночные сигналы, чтобы продукт был востребован, а позиционирование работало на рост.",
      },
      {
        title: "UX/UI дизайн",
        body: "Создаем интерфейсы, которые выглядят современно, остаются понятными в использовании и поддерживают бизнес-логику продукта.",
      },
      {
        title: "Разработка под заказ",
        body: "Собираем веб-платформы, мобильные приложения и внутренние системы с учетом процессов, целей и контекста конкретного бизнеса.",
      },
      {
        title: "Развертывание и инфраструктура",
        body: "Внедряем ПО в облачных средах и на стороне заказчика, выстраивая надежную доставку, масштабируемость и безопасность.",
      },
      {
        title: "Мобильные приложения",
        body: "Разрабатываем нативные и кроссплатформенные приложения с фокусом на производительность, удобство и реальную пользу для пользователей.",
      },
    ],
    productsKicker: "Продукты",
    productKicker: "Флагманский продукт",
    productTitle: "HRMS-платформа, которая ускоряет найм",
    productBody:
      "Stafflow от CatLogic помогает автоматизировать подбор персонала: от публикации вакансий и скрининга резюме до первичных интервью и рабочих процессов внутри команды найма.",
    productHighlights: [
      {
        label: "Контур",
        title: "ATS + адаптация",
        body: "Платформа закрывает путь кандидата от публикации вакансии до выхода сотрудника в рабочий процесс.",
      },
      {
        label: "Команда",
        title: "Единое рабочее пространство",
        body: "Подбор, коммуникации и ключевые действия команды собраны в одном понятном интерфейсе.",
      },
      {
        label: "Интеграции",
        title: "Готово к инфраструктуре бизнеса",
        body: "Stafflow легко встраивается в существующий контур и поддерживает автоматизацию на уровне процессов.",
      },
    ],
    productPills: ["ATS и подбор персонала", "Публикация вакансий", "Скрининг и интервью", "Адаптация и процессы"],
    hireKicker: "Новый продукт",
    hireTitle: "Умный и быстрый подбор персонала",
    hireBody:
      "Hire — ATS-платформа для команд по подбору персонала: вакансии, кандидаты, коммуникации, интервью, согласования и аналитика в одной системе.",
    hireHighlights: [
      {
        label: "ИИ",
        title: "Xenia AI — интервьюер",
        body: "Голосовые собеседования с кандидатами проходят прямо в браузере, без ручного участия рекрутера на первом этапе.",
      },
      {
        label: "Процесс",
        title: "Единый pipeline найма",
        body: "Вакансии, кандидаты и согласования собраны в одном рабочем пространстве — без таблиц и переписок в мессенджерах.",
      },
      {
        label: "Аналитика",
        title: "Данные о подборе",
        body: "Дашборд с ключевыми KPI найма и восемь видов отчетов для контроля эффективности команды.",
      },
    ],
    hirePills: ["ATS для рекрутеров", "AI-интервьюер Xenia", "Интеграции: HH, LinkedIn, Zoom", "Аналитика и отчеты"],
    aiKicker: "AI-практика",
    aiTitle: "Внедряем AI в бизнес без программистов и больших бюджетов",
    aiBody:
      "Практическое AI-внедрение от CatLogic для собственников и руководителей: диагностика процессов, обучение команд и запуск AI-инструментов с измеримым результатом — без долгой цифровой трансформации.",
    aiHighlights: [
      {
        label: "Диагностика",
        title: "Карта первого месяца внедрения",
        body: "За 30–40 минут находим задачи, где AI уже сейчас экономит время и деньги, и расставляем приоритеты по эффекту и риску.",
      },
      {
        label: "Обучение",
        title: "Практика вместо лекций про нейросети",
        body: "Учим руководителей и сотрудников на их реальных документах, отчетах, встречах и клиентских коммуникациях.",
      },
      {
        label: "Внедрение",
        title: "Короткий стек без зоопарка сервисов",
        body: "Собираем маршрут «задача → данные → AI → проверка → результат» и встраиваем его в процессы команды.",
      },
    ],
    aiPills: ["AI-диагностика бизнеса", "Обучение команд", "Внедрение AI-инструментов", "Белорусский B2B-контекст"],
    learnMore: "Узнать больше",
    contactsKicker: "Контакты",
    contactsTitle: "Обсудим продукт, сервис или пилотный запуск",
    contactsBody:
      "Мы ценим открытость и доступность для клиентов и партнеров. Если вам нужен новый цифровой продукт, редизайн существующего сервиса или сильная команда под запуск, начнем с короткого разговора.",
    formTitle: "Написать нам",
  },
  en: {
    pageTitle: "CatLogic — Digital Products, Stafflow HRMS & AI Rollout",
    metaDescription:
      "CatLogic is a Minsk-based product studio: custom software and UX/UI design, the Stafflow HRMS platform, and hands-on AI rollout for growing businesses.",
    metaKeywords:
      "CatLogic, Stafflow, HRMS platform, custom software development, UX UI design, mobile app development, AI implementation, HR automation, Minsk product studio",
    heroBadge: "CatLogic.tech • product engineering studio",
    heroTitle: "Digital products",
    heroTitleAccent: "that amplify teams",
    heroDescription:
      "We design interfaces, build platforms, and apply modern technology approaches so businesses can move from idea to working product faster.",
    primaryCta: "Discuss your project",
    secondaryCta: "View services",
    heroMetrics: [
      {
        title: "Full-cycle delivery",
        body: "From discovery and design to launch and product evolution.",
      },
      {
        title: "Remote-first workflow",
        body: "We work flexibly with teams across countries and time zones.",
      },
      {
        title: "AI-minded execution",
        body: "We use modern approaches to strengthen teams and workflows.",
      },
    ],
    panelTag: "How we work",
    panelTitle: "From idea to launch without unnecessary inertia",
    panelBody:
      "CatLogic brings together analysis, design, engineering, and rollout in one operating loop so solutions reach real impact faster.",
    deliveryFlow: [
      "Research, strategy, and architecture",
      "UX/UI design and interface planning",
      "Engineering, integrations, and cloud infrastructure",
      "Launch, team enablement, and support",
    ],
    missionKicker: "Mission",
    missionTitle: "Exceptional amplification of human capability through technology",
    missionBody:
      "At CatLogic, we turn ideas into practical digital systems that help companies grow, move faster, and feel confident in the tools they rely on.",
    whatWeDo: [
      {
        title: "Innovation and quality",
        body: "We explore new technologies and methodologies so CatLogic solutions stay current, resilient, and ready for future challenges.",
      },
      {
        title: "Speed and flexibility",
        body: "We launch quickly without sacrificing quality, staying adaptable to shifts in product direction, business priorities, and market reality.",
      },
      {
        title: "Partnership and trust",
        body: "We approach every engagement as a long-term collaboration built on transparency, respect, and measurable outcomes.",
      },
      {
        title: "Professionalism and craft",
        body: "Strong engineering and product expertise help us make mature technical decisions and carry them through to delivery.",
      },
    ],
    futureKicker: "Direction",
    futureTitle: "Building toward what is next",
    futureBody:
      "We believe technology can improve markets in meaningful ways. That is why CatLogic builds solutions that address current business needs while creating room for the next stage of growth.",
    servicesKicker: "Services",
    servicesTitle: "From research to product support",
    servicesBody:
      "We help teams launch and grow digital services by combining product logic, design, engineering discipline, and infrastructure reliability.",
    services: [
      {
        title: "Product discovery and analysis",
        body: "Before development starts, we shape a clear product picture by refining requirements, user flows, constraints, and architectural decisions.",
      },
      {
        title: "Market research",
        body: "We analyze audiences, competitors, and market signals so products are relevant and positioning supports growth.",
      },
      {
        title: "UX/UI design",
        body: "We design interfaces that feel modern, remain intuitive in use, and support the underlying business logic of the product.",
      },
      {
        title: "Custom software development",
        body: "We build web platforms, mobile applications, and internal systems around the processes, goals, and context of each business.",
      },
      {
        title: "Deployment and infrastructure",
        body: "We deliver software in cloud and customer environments with a focus on reliability, scalability, and operational safety.",
      },
      {
        title: "Mobile applications",
        body: "We develop native and cross-platform apps with a focus on performance, usability, and practical value for end users.",
      },
    ],
    productsKicker: "Products",
    productKicker: "Flagship product",
    productTitle: "An HRMS platform built to accelerate hiring",
    productBody:
      "Stafflow by CatLogic helps automate recruitment, from job publishing and resume screening to first-round interviews and workflows inside the hiring team.",
    productHighlights: [
      {
        label: "Scope",
        title: "ATS plus onboarding",
        body: "The platform covers the candidate journey from job publishing to a new hire entering the operating workflow.",
      },
      {
        label: "Team",
        title: "One working environment",
        body: "Recruitment, communication, and key team actions live inside one clear and connected interface.",
      },
      {
        label: "Integrations",
        title: "Ready for business infrastructure",
        body: "Stafflow fits into an existing stack and supports deeper automation at the process level.",
      },
    ],
    productPills: ["ATS and hiring", "Job distribution", "Screening and interviews", "Onboarding and workflows"],
    hireKicker: "New product",
    hireTitle: "Smart, fast hiring",
    hireBody:
      "Hire is an ATS platform for recruiting teams: job openings, candidates, communication, interviews, approvals, and analytics in one system.",
    hireHighlights: [
      {
        label: "AI",
        title: "Xenia AI interviewer",
        body: "Voice interviews with candidates run right in the browser, without a recruiter manually running the first round.",
      },
      {
        label: "Workflow",
        title: "One hiring pipeline",
        body: "Openings, candidates, and approvals live in a single workspace — no spreadsheets or scattered messenger threads.",
      },
      {
        label: "Analytics",
        title: "Hiring data",
        body: "A dashboard with key hiring KPIs and eight report types to track the recruiting team's performance.",
      },
    ],
    hirePills: ["ATS for recruiters", "Xenia AI interviewer", "Integrations: HH, LinkedIn, Zoom", "Analytics and reports"],
    aiKicker: "AI practice",
    aiTitle: "Implementing AI into business without engineers or big budgets",
    aiBody:
      "Hands-on AI rollout from CatLogic for owners and leaders: process diagnostics, team training, and AI tooling launched with a measurable result — without a lengthy digital transformation.",
    aiHighlights: [
      {
        label: "Diagnostics",
        title: "A first-month rollout map",
        body: "In 30–40 minutes we find the tasks where AI already saves time and money, and rank them by impact and risk.",
      },
      {
        label: "Training",
        title: "Practice instead of lectures about neural networks",
        body: "We train leaders and employees on their own real documents, reports, meetings, and client communication.",
      },
      {
        label: "Rollout",
        title: "A short stack without a service zoo",
        body: "We build a task → data → AI → review → result route and embed it into the team's day-to-day process.",
      },
    ],
    aiPills: ["AI business diagnostics", "Team training", "AI tool rollout", "Belarusian B2B context"],
    learnMore: "Learn more",
    contactsKicker: "Contacts",
    contactsTitle: "Let us discuss a product, service, or pilot launch",
    contactsBody:
      "We value openness and accessibility for clients and partners. If you need a new digital product, a redesign of an existing service, or a strong team for launch, we can start with a short conversation.",
    formTitle: "Send us a message",
  },
};

export const ABOUT_COPY: Record<SiteLocale, AboutCopy> = {
  ru: {
    pageTitle: "О компании CatLogic — продуктовая студия и AI-практика",
    metaDescription:
      "CatLogic — команда инженеров и продуктовых дизайнеров из Минска. С 2024 года строим цифровые продукты, HR-tech решения и AI-практику для бизнеса.",
    metaKeywords: "CatLogic, о компании, продуктовая студия, разработка ПО, Сергей Шарков, HR-tech, Минск, команда разработки",
    badge: "О CatLogic",
    heroTitle: "Строим цифровые продукты с инженерной глубиной и продуктовой ясностью",
    heroBody:
      "CatLogic объединяет стратегию, дизайн, разработку и AI-подходы в один рабочий контур. Мы помогаем компаниям быстрее переходить от идеи к устойчивому продукту и делать этот путь более понятным, управляемым и технологически зрелым.",
    highlights: [
      {
        value: "2024",
        label: "старт CatLogic",
        body: "Компания появилась как ответ на запрос бизнеса на более зрелые и современные ИТ-решения.",
      },
      {
        value: "30+",
        label: "лет в IT-бизнесе",
        body: "Опыт основателя и команды охватывает путь от технологических стартапов до устойчивых enterprise-систем.",
      },
      {
        value: "Remote-first",
        label: "формат работы",
        body: "Подключаемся к проектам гибко, быстро и без лишней операционной инерции.",
      },
    ],
    panelLabel: "Что для нас важно",
    panelTitle: "CatLogic смотрит на продукт как на систему, а не набор задач",
    panelBody:
      "Мы не рассматриваем технологии как самоцель. Для нас они работают тогда, когда помогают бизнесу двигаться быстрее, командам принимать лучшие решения, а пользователям получать понятный и качественный опыт.",
    focusPoints: [
      "Инженерная глубина и продуктовая ясность",
      "UX/UI, разработка и AI-подходы в одном рабочем контуре",
      "Решения, которые готовы к росту, а не только к запуску",
      "Партнерский стиль работы вместо формального аутсорса",
    ],
    storyKicker: "История и подход",
    storyTitle: "Кто мы и почему работаем именно так",
    storyCards: [
      {
        eyebrow: "Основание",
        title: "CatLogic появилась как прагматичный ответ на запрос рынка",
        body: "Компания основана Сергеем Шарковым в 2024 году. Идея CatLogic выросла из практического опыта: бизнесу нужны не просто подрядчики на разработку, а команды, которые умеют соединять стратегию, дизайн, технологию и запуск.",
      },
      {
        eyebrow: "Название",
        title: "В CatLogic сочетаются гибкость, наблюдательность и логика",
        body: "Название отражает наш подход к работе. Мы ценим адаптивность и точность, умеем быстро считывать контекст и принимаем решения на основе инженерного мышления, а не случайных компромиссов.",
      },
      {
        eyebrow: "Команда",
        title: "Собрали специалистов, которые смотрят на продукт шире кода",
        body: "В CatLogic работают люди с сильной экспертизой в разработке, UX/UI, управлении продуктом и AI-направлениях. Благодаря этому мы проектируем решения системно, а не закрываем задачи по отдельности.",
      },
      {
        eyebrow: "Опыт",
        title: "Сильная основа строится на многолетней практике",
        body: "Опыт основателя и ключевых участников команды охватывает более трех десятилетий работы в IT-бизнесе: от запуска стартапов до развития зрелых цифровых продуктов и внутренних платформ.",
      },
      {
        eyebrow: "Фокус",
        title: "Нам близки сервисы, которые реально усиливают компании",
        body: "Мы работаем над внутренними платформами, digital-сервисами, HR-tech решениями и инструментами автоматизации. Нас интересуют продукты, которые делают процессы прозрачнее, команды сильнее, а рост бизнеса устойчивее.",
      },
      {
        eyebrow: "Вектор",
        title: "Мы строим решения, рассчитанные на следующий этап роста",
        body: "CatLogic нацелена не только на быстрый запуск, но и на последующее развитие продукта. Архитектура, интерфейсы и процессы должны выдерживать масштабирование и не превращаться в тормоз через полгода.",
      },
    ],
    positionKicker: "Позиция",
    positionTitle: "Нам важны не просто красивые технологии, а решения, которые выдерживают реальную нагрузку бизнеса",
    positionBody:
      "Именно поэтому мы соединяем продуктовую логику, сильную разработку и современный взгляд на автоматизацию. Такой подход помогает создавать системы, которые не только запускаются, но и спокойно растут вместе с компанией.",
    principles: [
      {
        title: "Скорость без хаоса",
        body: "Двигаемся быстро, но не жертвуем качеством решений и понятностью процессов.",
      },
      {
        title: "Технологии ради эффекта",
        body: "Используем современные подходы только там, где они реально дают бизнесу преимущество.",
      },
      {
        title: "Партнерский подход",
        body: "Работаем так, чтобы быть полезной частью продукта и команды, а не внешней формальностью.",
      },
    ],
    nextKicker: "Дальше",
    ctaTitle: "Ищете команду для запуска продукта или хотите присоединиться к нам?",
    ctaBody: "Мы открыты к новым проектам, партнерствам и сильным людям, которые хотят делать полезные цифровые продукты.",
    careersCta: "Смотреть вакансии",
    contactCta: "Связаться с нами",
  },
  en: {
    pageTitle: "About CatLogic — Product Studio & AI Practice",
    metaDescription:
      "CatLogic is a Minsk-based team of engineers and product designers. Since 2024 we've been building digital products, HR-tech solutions, and an AI practice.",
    metaKeywords: "CatLogic, about us, product studio, software engineering, Sergey Sharkov, HR tech, Minsk IT company, engineering team",
    badge: "About CatLogic",
    heroTitle: "We build digital products with engineering depth and product clarity",
    heroBody:
      "CatLogic brings strategy, design, development, and AI-minded execution into one operating model. We help companies move from idea to sustainable product faster and make that path clearer, more manageable, and more technologically mature.",
    highlights: [
      {
        value: "2024",
        label: "CatLogic launch",
        body: "The company was created in response to business demand for more mature and more modern digital solutions.",
      },
      {
        value: "30+",
        label: "years in IT business",
        body: "The founder and team bring experience spanning everything from technology startups to durable enterprise systems.",
      },
      {
        value: "Remote-first",
        label: "working format",
        body: "We join projects flexibly, quickly, and without unnecessary operational drag.",
      },
    ],
    panelLabel: "What matters to us",
    panelTitle: "CatLogic sees a product as a system, not a checklist of tasks",
    panelBody:
      "We do not treat technology as an end in itself. For us, it matters when it helps businesses move faster, enables teams to make better decisions, and gives users a clear, high-quality experience.",
    focusPoints: [
      "Engineering depth with product clarity",
      "UX/UI, development, and AI approaches in one working loop",
      "Solutions designed for growth, not only for launch",
      "A partnership model instead of formal outsourcing",
    ],
    storyKicker: "Story and approach",
    storyTitle: "Who we are and why we work this way",
    storyCards: [
      {
        eyebrow: "Foundation",
        title: "CatLogic emerged as a pragmatic answer to market demand",
        body: "The company was founded by Sergey Sharkov in 2024. The idea grew out of hands-on experience: businesses need more than implementation vendors, they need teams that can connect strategy, design, technology, and launch.",
      },
      {
        eyebrow: "Name",
        title: "CatLogic combines adaptability, observation, and logic",
        body: "The name reflects our working style. We value adaptability and precision, read context quickly, and make decisions through engineering reasoning rather than accidental compromise.",
      },
      {
        eyebrow: "Team",
        title: "We brought together specialists who see the product beyond code",
        body: "CatLogic includes people with strong expertise in software development, UX/UI, product management, and AI-related directions. That lets us design solutions systemically instead of solving isolated tasks.",
      },
      {
        eyebrow: "Experience",
        title: "A strong foundation comes from long-term practice",
        body: "The founder and core team members bring more than three decades of IT business experience, from startup launches to mature digital products and internal platforms.",
      },
      {
        eyebrow: "Focus",
        title: "We are drawn to services that genuinely strengthen companies",
        body: "We work on internal platforms, digital services, HR-tech products, and automation tools. We care about products that make processes clearer, teams stronger, and business growth more sustainable.",
      },
      {
        eyebrow: "Direction",
        title: "We build solutions for the next stage of growth",
        body: "CatLogic is focused not only on rapid launch, but also on what follows. Architecture, interfaces, and processes should support scale instead of becoming friction six months later.",
      },
    ],
    positionKicker: "Position",
    positionTitle: "What matters to us is not trendy technology, but solutions that hold up under real business load",
    positionBody:
      "That is why we combine product logic, strong engineering, and a modern view of automation. This approach helps create systems that not only launch, but continue growing calmly with the company.",
    principles: [
      {
        title: "Speed without chaos",
        body: "We move quickly without sacrificing solution quality or process clarity.",
      },
      {
        title: "Technology for real effect",
        body: "We use modern approaches only where they create a tangible advantage for the business.",
      },
      {
        title: "Partnership mindset",
        body: "We work to become a useful part of the product and the team, not an external formality.",
      },
    ],
    nextKicker: "Next",
    ctaTitle: "Looking for a team to launch a product or interested in joining us?",
    ctaBody: "We are open to new projects, partnerships, and strong people who want to build digital products that matter.",
    careersCta: "View careers",
    contactCta: "Contact us",
  },
};

export const AI_COPY: Record<SiteLocale, AiCopy> = {
  ru: {
    pageTitle: "AI-внедрение для бизнеса без программистов — CatLogic Tech",
    metaDescription:
      "AI-внедрение для бизнеса от CatLogic и эксперта Никиты Карпука: диагностика процессов, обучение команд и запуск AI-инструментов с измеримым результатом.",
    metaKeywords:
      "AI внедрение, искусственный интеллект для бизнеса, Никита Карпук, karpuk.by, AI диагностика бизнеса, обучение команд AI, CatLogic Tech, автоматизация процессов AI",
    badge: "AI-практика CatLogic",
    heroTitle: "Внедряем AI в бизнес без программистов и больших бюджетов",
    heroBody:
      "Практическое AI-внедрение для собственников и руководителей: диагностика процессов, обучение команд и запуск AI-инструментов с измеримым результатом — без долгой цифровой трансформации.",
    primaryCta: "Обсудить внедрение",
    secondaryCta: "Как это работает",
    expertKicker: "Кто ведёт практику",
    expertName: "Никита Карпук",
    expertRole: "Эксперт CatLogic Tech по AI-внедрению",
    expertBody:
      "Помогает собственникам, руководителям и командам внедрять AI в реальные бизнес-процессы — от HR и обучения до аналитики, продаж и внутренних коммуникаций. Учит на конкретных задачах компании, а не на абстрактных лекциях о нейросетях, и держит фокус на белорусском B2B-контексте: доступность сервисов, VPN и безопасность данных.",
    expertHighlights: ["Экспертиза в HR и обучении", "Десятки проведенных тренингов", "Публичная AI-практика", "Живой стек AI-моделей и сервисов"],
    painKicker: "Знакомые ситуации",
    painTitle: "С какими проблемами руководители приходят к нам",
    painPoints: [
      {
        title: "Руководители тонут в операционке",
        body: "Дорогое время собственников и топ-команды уходит на однотипные документы, встречи, отчеты и контроль.",
        response: "Находим ручные процессы, где AI снимает нагрузку и возвращает фокус на рост бизнеса.",
      },
      {
        title: "AI уже используют хаотично",
        body: "Сотрудники пробуют разные сервисы без правил, что повышает риск утечки данных и разброс качества.",
        response: "Вводим понятные правила, безопасные сценарии, корпоративные шаблоны и базу промптов.",
      },
      {
        title: "Команда сопротивляется",
        body: "Люди боятся, что AI их заменит, и поэтому не хотят разбираться в инструментах.",
        response: "Обучаем на реальных задачах команды, чтобы AI стал помощником, а не угрозой.",
      },
      {
        title: "Решения принимаются на ощущениях",
        body: "На сбор данных и подготовку управленческой аналитики не хватает времени.",
        response: "Показываем, как готовить встречи, анализировать документы и собирать данные за часы, а не дни.",
      },
    ],
    processKicker: "Как проходит внедрение",
    processTitle: "Понятный маршрут от диагностики до результата",
    processBody: "Разбираем процессы и отделы, где AI даст эффект без длинной цифровой трансформации.",
    processSteps: [
      {
        title: "Диагностика",
        body: "Считаем частоту задач, стоимость ручной работы и риск ошибки, чтобы выбирать не модные, а прибыльные сценарии.",
      },
      {
        title: "Выбор инструментов",
        body: "Собираем рабочий стек из LLM, промптов, шаблонов и Telegram-сценариев — без зоопарка сервисов.",
      },
      {
        title: "Внедрение в процессы",
        body: "Описываем маршрут «задача → данные → AI → проверка → результат», чтобы команда применяла AI каждый день.",
      },
      {
        title: "Обучение команды",
        body: "Обучаем руководителей и сотрудников на их документах, отчетах, встречах и клиентских коммуникациях.",
      },
    ],
    tiersKicker: "Форматы работы",
    tiersTitle: "Выберите формат под задачу и масштаб",
    tiersBody: "От разбора одной дорогой рутинной задачи до корпоративной программы и кастомных AI-решений.",
    tiers: [
      {
        label: "Быстрый старт",
        title: "Обучение и внедрение",
        body: "Практическое обучение на реальных задачах команды. Берем дорогую рутинную задачу и автоматизируем её.",
        outcome: "Экономия до 20 человеко-часов в неделю.",
      },
      {
        label: "Управляемый масштаб",
        title: "Корпоративная программа",
        body: "Находим зоны неэффективности и оптимизируем процессы с помощью AI-подходов.",
        outcome: "AI становится предсказуемым инструментом всех подразделений.",
      },
      {
        label: "Безопасность и уникальность",
        title: "Кастомные AI-решения",
        body: "Создаем интеллектуальный актив компании, адаптированный под ваши бизнес-процессы, с индивидуальным аудитом.",
        outcome: "Собственный цифровой актив без зависимости от внешних подписок.",
      },
      {
        label: "События и команда",
        title: "Выступление / семинар",
        body: "Живые демо, реальные кейсы и практические выводы без абстракций для управленческой сессии.",
        outcome: "Аудитория уходит с конкретными идеями, а не общими впечатлениями.",
      },
    ],
    ctaKicker: "Следующий шаг",
    ctaTitle: "Готовы обсудить, где AI даст эффект в вашей компании?",
    ctaBody:
      "Начнем с короткой AI-диагностики: 30–40 минут, чтобы увидеть карту задач и процессов и понять, с чего стартовать.",
    ctaPrimary: "Обсудить внедрение",
    ctaSecondary: "Смотреть услуги",
  },
  en: {
    pageTitle: "AI Implementation for Business Without Engineers — CatLogic Tech",
    metaDescription:
      "AI rollout for business by CatLogic and expert Nikita Karpuk: process diagnostics, team training, and AI tooling launched with a measurable result.",
    metaKeywords:
      "AI implementation, AI for business, Nikita Karpuk, karpuk.by, AI business diagnostics, AI team training, CatLogic Tech, business process automation AI",
    badge: "AI practice by CatLogic",
    heroTitle: "Implementing AI into business without engineers or big budgets",
    heroBody:
      "Hands-on AI rollout for owners and leaders: process diagnostics, team training, and AI tooling launched with a measurable result — without a lengthy digital transformation.",
    primaryCta: "Discuss a rollout",
    secondaryCta: "See how it works",
    expertKicker: "Who leads the practice",
    expertName: "Nikita Karpuk",
    expertRole: "CatLogic Tech expert in AI rollout",
    expertBody:
      "Helps owners, leaders, and teams put AI to work in real business processes — from HR and training to analytics, sales, and internal communication. Teaches on the company's own tasks rather than abstract lectures about neural networks, with a steady focus on the Belarusian B2B context: service availability, VPN, and data security.",
    expertHighlights: ["HR and training expertise", "Dozens of workshops delivered", "A public AI practice", "A living stack of AI models and tools"],
    painKicker: "Familiar situations",
    painTitle: "The problems leaders bring to us",
    painPoints: [
      {
        title: "Leaders are drowning in operations",
        body: "Owners' and leadership's expensive time goes into repetitive documents, meetings, reports, and oversight.",
        response: "We find the manual processes where AI removes the load and returns focus to business growth.",
      },
      {
        title: "AI is already used chaotically",
        body: "Employees try different services without rules, raising the risk of data leaks and inconsistent quality.",
        response: "We introduce clear rules, safe scenarios, corporate templates, and a shared prompt library.",
      },
      {
        title: "The team resists",
        body: "People fear AI will replace them, so they avoid learning the tools.",
        response: "We train on the team's real tasks so AI becomes a helper rather than a threat.",
      },
      {
        title: "Decisions are made on gut feeling",
        body: "There isn't enough time to gather data and prepare management analytics.",
        response: "We show how to prepare meetings, analyze documents, and gather data in hours, not days.",
      },
    ],
    processKicker: "How a rollout works",
    processTitle: "A clear route from diagnostics to results",
    processBody: "We break down the processes and departments where AI delivers impact without a lengthy digital transformation.",
    processSteps: [
      {
        title: "Diagnostics",
        body: "We measure task frequency, the cost of manual work, and error risk to pick profitable scenarios, not trendy ones.",
      },
      {
        title: "Tool selection",
        body: "We assemble a working stack of LLMs, prompts, templates, and Telegram scenarios — without a service zoo.",
      },
      {
        title: "Process rollout",
        body: "We map a task → data → AI → review → result route so the team applies AI every day.",
      },
      {
        title: "Team training",
        body: "We train leaders and employees on their own documents, reports, meetings, and client communication.",
      },
    ],
    tiersKicker: "Engagement formats",
    tiersTitle: "Pick a format that fits the task and the scale",
    tiersBody: "From tackling a single expensive routine task to a corporate program and custom AI solutions.",
    tiers: [
      {
        label: "Quick start",
        title: "Training and rollout",
        body: "Hands-on training on the team's real tasks. We take one expensive routine task and automate it.",
        outcome: "Up to 20 person-hours saved per week.",
      },
      {
        label: "Managed scale",
        title: "Corporate program",
        body: "We find zones of inefficiency and optimize processes using AI approaches.",
        outcome: "AI becomes a predictable tool across every department.",
      },
      {
        label: "Security and uniqueness",
        title: "Custom AI solutions",
        body: "We build an intelligent asset for the company, adapted to your business processes, with an individual audit.",
        outcome: "A proprietary digital asset with no dependency on external subscriptions.",
      },
      {
        label: "Events and teams",
        title: "Talk / workshop",
        body: "Live demos, real cases, and practical takeaways without abstractions for a management session.",
        outcome: "The audience leaves with concrete ideas, not general impressions.",
      },
    ],
    ctaKicker: "Next step",
    ctaTitle: "Ready to discuss where AI can make an impact in your company?",
    ctaBody: "We start with a short AI diagnostic: 30–40 minutes to map tasks and processes and see where to begin.",
    ctaPrimary: "Discuss a rollout",
    ctaSecondary: "View services",
  },
};

export const PRIVACY_POLICY_COPY: Record<SiteLocale, PolicyCopy> = {
  ru: {
    badge: "Правовая информация",
    title: "Политика конфиденциальности и обработки персональных данных",
    pageTitle: "CatLogic | Политика конфиденциальности и обработки персональных данных",
    description:
      "Документ описывает принципы обработки персональных данных на платформе Stafflow и роль CatLogic как уполномоченного лица, действующего по поручению операторов.",
    summaryTitle: "Прозрачные правила обработки данных",
    summaryBody:
      "Мы сохранили юридическое содержание документа, но оформили страницу в более современном и удобном для чтения формате. Ниже размещен полный текст политики.",
    sections: [
      { id: "general", label: "Общие положения" },
      { id: "processing", label: "Условия обработки" },
      { id: "rights", label: "Права субъектов" },
      { id: "security", label: "Защита данных" },
      { id: "transfer", label: "Передача данных" },
      { id: "final", label: "Заключительные положения" },
      { id: "details", label: "Реквизиты" },
    ],
    content: `

## 1. Общие положения

**1.1.** Платформа **Stafflow**, владельцем которой является **ООО «Кэтлоджик»** (далее – Платформа), выступает уполномоченным лицом, осуществляющим обработку персональных данных по поручению операторов – организаций-заказчиков (работодателей).

**1.2.** Персональные данные пользователей обрабатываются исключительно с целью выполнения задач, порученных оператором (заказчиком платформы) в рамках предоставляемых услуг, а именно:
- ведения кадрового учета;
- осуществления процессов подбора и оценки персонала;
- проведения обучения и оценки компетенций;
- организации коммуникаций между сотрудниками.

**1.3.** Персональные данные, обрабатываемые Платформой, могут включать:
- фамилию, имя, отчество,
- дату рождения,
- контактные данные (телефон, email),
- сведения об образовании, трудовом стаже
- и другие сведения, необходимые для выполнения порученных задач.

## 2. Условия обработки персональных данных

**2.1.** Обработка персональных данных осуществляется исключительно по поручению операторов персональных данных (организаций-заказчиков услуг Платформы) на основании заключенных с ними договоров.

**2.2.** Персональные данные обрабатываются с согласия субъектов персональных данных, предоставленного оператору (организации-заказчику) в порядке, предусмотренном законодательством Республики Беларусь.

**2.3.** Срок обработки персональных данных ограничен периодом действия договоров с операторами и достижением целей, указанных в пункте 1.1 настоящей Политики.

## 3. Права субъектов персональных данных

**3.1.** Субъект персональных данных вправе:
- получать от операторов информацию об обработке своих персональных данных;
- требовать от операторов уточнения, обновления, блокирования или удаления своих персональных данных;
- отозвать согласие на обработку персональных данных, предоставленное операторам;
- обжаловать действия или бездействие операторов.

**3.2.** Реализация указанных прав осуществляется через оператора (организацию-заказчика), от имени и в интересах которого Платформа обрабатывает персональные данные.

## 4. Меры по защите персональных данных

**4.1.** Платформа реализует технические, программные и организационные меры защиты персональных данных в соответствии с требованиями законодательства Республики Беларусь.

**4.2.** Персональные данные обрабатываются с использованием сертифицированных средств защиты информации.

**4.3.** Все сотрудники Платформы, имеющие доступ к персональным данным, подписывают обязательства о конфиденциальности.

**4.4.** В случае возникновения угрозы или факта нарушения защиты персональных данных Платформа незамедлительно уведомляет операторов и предпринимает меры по устранению последствий.

## 5. Передача персональных данных

**5.1.** Передача персональных данных третьим лицам возможна только по поручению и с согласия оператора.

**5.2.** Трансграничная передача персональных данных осуществляется в строгом соответствии с законодательством Республики Беларусь.

## 6. Заключительные положения

**6.1.** Настоящая Политика размещается в открытом доступе на сайте Платформы.

**6.2.** Изменения в Политику конфиденциальности вносятся в соответствии с законодательством Республики Беларусь.  

## Реквизиты

Настоящая политика конфиденциальности разработана в соответствии с **Законом Республики Беларусь «О защите персональных данных» от 7 мая 2021 г. № 99-З** и вступает в силу с момента её публикации на сайте Продукта.

**ООО «Кэтлоджик»**  
Юридический адрес: Республика Беларусь, г. Минск, 220073, ул. Харьковская, д. 15, пом. 311
`,
  },
  en: {
    badge: "Legal information",
    title: "Privacy and personal data processing policy",
    pageTitle: "CatLogic | Privacy and personal data processing policy",
    description:
      "This document explains the principles of personal data processing on the Stafflow platform and CatLogic's role as an authorized processor acting on behalf of operators.",
    summaryTitle: "Clear rules for data processing",
    summaryBody:
      "The legal substance of the document has been preserved, while the page has been formatted in a more modern and readable way. The full text of the policy is provided below.",
    sections: [
      { id: "general", label: "General provisions" },
      { id: "processing", label: "Processing conditions" },
      { id: "rights", label: "Rights of data subjects" },
      { id: "security", label: "Data protection" },
      { id: "transfer", label: "Data transfer" },
      { id: "final", label: "Final provisions" },
      { id: "details", label: "Company details" },
    ],
    content: `

## 1. General provisions

**1.1.** The **Stafflow** platform, owned by **CatLogic LLC** (hereinafter, the Platform), acts as an authorized processor carrying out personal data processing on behalf of operators, namely customer organizations and employers.

**1.2.** Users' personal data is processed solely for the purpose of performing tasks assigned by the operator, within the scope of the services provided, including:
- maintaining HR records;
- supporting recruitment and personnel assessment processes;
- conducting training and competency assessment;
- organizing communication between employees.

**1.3.** Personal data processed by the Platform may include:
- surname, first name, patronymic;
- date of birth;
- contact details, including phone number and email;
- information about education and work experience;
- and other information required to perform the assigned tasks.

## 2. Conditions for personal data processing

**2.1.** Personal data is processed exclusively on behalf of personal data operators, namely customer organizations using the Platform's services, under contracts concluded with them.

**2.2.** Personal data is processed on the basis of consent granted by the data subjects to the operator, in accordance with the legislation of the Republic of Belarus.

**2.3.** The period of personal data processing is limited by the duration of contracts with operators and the achievement of the purposes specified in clause 1.1 of this Policy.

## 3. Rights of personal data subjects

**3.1.** A personal data subject has the right to:
- receive information from operators about the processing of their personal data;
- request clarification, updating, blocking, or deletion of their personal data by operators;
- withdraw consent to the processing of personal data previously granted to operators;
- appeal actions or inaction of operators.

**3.2.** The exercise of these rights is carried out through the operator, namely the customer organization on whose behalf and in whose interests the Platform processes personal data.

## 4. Measures for the protection of personal data

**4.1.** The Platform implements technical, software, and organizational measures to protect personal data in accordance with the legislation of the Republic of Belarus.

**4.2.** Personal data is processed using certified information security tools.

**4.3.** All Platform employees who have access to personal data sign confidentiality undertakings.

**4.4.** In the event of a threat to personal data protection or an actual breach, the Platform promptly notifies operators and takes measures to eliminate the consequences.

## 5. Transfer of personal data

**5.1.** Personal data may be transferred to third parties only on the operator's instruction and with the operator's consent.

**5.2.** Cross-border transfer of personal data is carried out in strict compliance with the legislation of the Republic of Belarus.

## 6. Final provisions

**6.1.** This Policy is made publicly available on the Platform website.

**6.2.** Amendments to the privacy policy are introduced in accordance with the legislation of the Republic of Belarus.

## Company details

This privacy policy has been drafted in accordance with the **Law of the Republic of Belarus "On Personal Data Protection" dated May 7, 2021, No. 99-Z** and enters into force from the moment it is published on the Product website.

**CatLogic LLC**  
Registered address: Republic of Belarus, Minsk, 220073, Kharkovskaya Street 15, office 311
`,
  },
};

export const COOKIES_POLICY_COPY: Record<SiteLocale, PolicyCopy> = {
  ru: {
    badge: "Правовая информация",
    title: "Политика использования файлов cookie",
    pageTitle: "CatLogic | Политика использования файлов cookie",
    description:
      "Страница объясняет, какие cookie и аналогичные технологии используются на catlogic.tech и stafflow.by, для каких задач они нужны и как пользователь может управлять ими в браузере.",
    summaryTitle: "Понятное описание работы cookie",
    summaryBody:
      "Структура страницы обновлена в том же визуальном стиле, что и другие ключевые разделы сайта. Полный текст политики размещен ниже без изменения его смысла.",
    sections: [
      { id: "general", label: "Общие положения" },
      { id: "definition", label: "Что такое cookie" },
      { id: "usage", label: "Как мы используем cookie" },
      { id: "management", label: "Управление cookie" },
      { id: "changes", label: "Изменения" },
      { id: "contacts", label: "Контактная информация" },
    ],
    content: `## 1. Общие положения

Настоящая политика использования файлов cookie (далее – Политика) описывает, как сайт stafflow.by, catlogic.tech (далее – Сайт) использует файлы cookie и аналогичные технологии для улучшения работы пользователей и обеспечения предоставления качественных услуг.

## 2. Что такое файлы cookie?

Файлы cookie – это небольшие текстовые файлы, которые сохраняются на устройстве пользователя (компьютере, планшете, смартфоне) при посещении веб-сайтов. Файлы cookie позволяют сайтам запоминать действия и предпочтения пользователя (например, логин, язык, размер шрифта и другие настройки отображения), чтобы пользователю не нужно было заново вводить их при каждом посещении сайта или переходе с одной страницы на другую.

## 3. Как мы используем файлы cookie?

Мы используем файлы cookie для различных целей:

- **Необходимые файлы cookie**: Эти файлы cookie необходимы для функционирования Сайта. Они позволяют вам перемещаться по Сайту и использовать его функции, такие как доступ к защищенным областям Сайта.
- **Функциональные файлы cookie**: Эти файлы cookie позволяют Сайту запоминать сделанные вами выборы (например, ваше имя пользователя, язык или регион, в котором вы находитесь) и предоставлять улучшенные, более персонализированные функции.
- **Аналитические файлы cookie**: Эти файлы cookie собирают информацию о том, как посетители используют Сайт, например, какие страницы они посещают чаще всего и получают ли они сообщения об ошибках с веб-страниц. Эти файлы cookie используются для улучшения работы Сайта.
- **Рекламные файлы cookie**: Эти файлы cookie используются для показа рекламных объявлений, которые более релевантны для вас и ваших интересов. Они также используются для ограничения числа показов рекламы и помогают измерять эффективность рекламных кампаний.

## 4. Управление файлами cookie

### 4.1. Как вы можете управлять файлами cookie?

Вы можете контролировать и/или удалять файлы cookie по своему усмотрению – подробности смотрите на сайте [aboutcookies.org](https://www.aboutcookies.org). Вы можете удалить все файлы cookie, которые уже находятся на вашем компьютере, а также настроить большинство браузеров на предотвращение их размещения. Однако в этом случае вам, возможно, придётся вручную изменять некоторые параметры при каждом посещении сайта, и некоторые функции могут не работать.

### 4.2. Изменение настроек cookie в браузере

Большинство веб-браузеров позволяют контролировать файлы cookie через настройки браузера. Вы можете настроить браузер так, чтобы он блокировал файлы cookie или предупреждал вас, когда файлы cookie отправляются на ваше устройство. Обратитесь к документации вашего браузера, чтобы узнать, как изменить настройки cookie.

### 4.3. Отказ от аналитических и рекламных файлов cookie

Чтобы отказаться от использования аналитических и рекламных файлов cookie, вы можете воспользоваться инструментами, предлагаемыми сервисами аналитики и рекламными платформами. Например, вы можете посетить сайты таких организаций, как [Network Advertising Initiative (NAI)](https://www.networkadvertising.org) или [Digital Advertising Alliance (DAA)](https://www.aboutads.info), чтобы получить информацию о том, как отказаться от получения рекламы, основанной на ваших интересах.

## 5. Изменения в Политике

Мы можем время от времени обновлять настоящую Политику, чтобы отразить изменения в используемых нами файлах cookie или по другим операционным, юридическим или нормативным причинам. Мы рекомендуем регулярно просматривать эту Политику, чтобы быть в курсе изменений.

## 6. Контактная информация

Если у вас есть вопросы по поводу настоящей Политики или нашего использования файлов cookie, пожалуйста, свяжитесь с нами по электронной почте: [info@catlogic.tech](mailto:info@catlogic.tech).`,
  },
  en: {
    badge: "Legal information",
    title: "Cookie policy",
    pageTitle: "CatLogic | Cookie policy",
    description:
      "This page explains which cookies and similar technologies are used on catlogic.tech and stafflow.by, what they are needed for, and how users can manage them in their browser.",
    summaryTitle: "A clear explanation of how cookies work",
    summaryBody:
      "The page structure has been refreshed in the same visual style as the rest of the key website sections. The full policy text is provided below without changing its meaning.",
    sections: [
      { id: "general", label: "General provisions" },
      { id: "definition", label: "What cookies are" },
      { id: "usage", label: "How we use cookies" },
      { id: "management", label: "Cookie management" },
      { id: "changes", label: "Changes" },
      { id: "contacts", label: "Contact information" },
    ],
    content: `## 1. General provisions

This cookie policy (the Policy) describes how the websites stafflow.by and catlogic.tech (the Site) use cookies and similar technologies to improve user experience and ensure the proper provision of services.

## 2. What are cookies?

Cookies are small text files stored on a user's device, such as a computer, tablet, or smartphone, when websites are visited. Cookies allow websites to remember a user's actions and preferences, for example login details, language, font size, and other display settings, so that these do not need to be entered again each time the site is visited or when moving from one page to another.

## 3. How we use cookies

We use cookies for a range of purposes:

- **Essential cookies**: These cookies are necessary for the Site to function. They enable you to move around the Site and use features such as access to secure areas.
- **Functional cookies**: These cookies allow the Site to remember choices you make, for example your username, language, or region, and provide enhanced and more personalized features.
- **Analytical cookies**: These cookies collect information about how visitors use the Site, for example which pages are visited most often and whether users receive error messages from web pages. These cookies are used to improve Site performance.
- **Advertising cookies**: These cookies are used to show advertisements that are more relevant to you and your interests. They are also used to limit the number of times an ad is shown and help measure the effectiveness of advertising campaigns.

## 4. Managing cookies

### 4.1. How you can manage cookies

You can control and or delete cookies as you wish. For more details, visit [aboutcookies.org](https://www.aboutcookies.org). You can delete all cookies already stored on your computer and configure most browsers to prevent them from being placed. However, if you do so, you may have to manually adjust some preferences every time you visit the Site, and some features may not function properly.

### 4.2. Changing cookie settings in your browser

Most web browsers allow you to control cookies through browser settings. You can configure your browser to block cookies or warn you when cookies are being sent to your device. Please refer to your browser documentation to learn how to change your cookie settings.

### 4.3. Opting out of analytical and advertising cookies

To opt out of analytical and advertising cookies, you can use the tools offered by analytics services and advertising platforms. For example, you can visit the websites of organizations such as [Network Advertising Initiative (NAI)](https://www.networkadvertising.org) or [Digital Advertising Alliance (DAA)](https://www.aboutads.info) to learn how to opt out of interest-based advertising.

## 5. Changes to the Policy

We may update this Policy from time to time to reflect changes in the cookies we use or for other operational, legal, or regulatory reasons. We recommend reviewing this Policy regularly so that you remain informed about any updates.

## 6. Contact information

If you have any questions about this Policy or our use of cookies, please contact us by email at [info@catlogic.tech](mailto:info@catlogic.tech).`,
  },
};
